import { Controller } from "@hotwired/stimulus"

// テキスト入力を Google カレンダー風の時刻コンボボックスにする。
// クリック/フォーカスで5分刻みの候補リスト（00:00〜23:55）を開き、クリックで選択できる。
// 「1430」「930」「14」のような直接入力も受け付け、確定時に "HH:MM" へ正規化する
// （5分刻み以外の時刻も入力可能。候補リストはあくまで入力補助）。
//
// 使い方:
//   <div class="relative" data-controller="time-picker">
//     <%= f.text_field :scheduled_at, value: ..., autocomplete: "off",
//           data: { time_picker_target: "input" } %>
//   </div>
export default class extends Controller {
  static targets = ["input"]

  // 候補の刻み幅（分）。Google カレンダー等に合わせた5分刻み。
  static STEP_MINUTES = 5
  // 値が空のまま開いたときにスクロール位置の基準にする時刻（面接が多い日中帯の先頭）。
  static DEFAULT_SCROLL_TIME = "10:00"

  connect() {
    this.input = this.inputTarget
    this.options = this.buildTimeOptions()
    this.lastValidValue = this.normalizeTime(this.input.value) ?? ""
    this.input.value = this.lastValidValue
    this.activeIndex = -1

    this.buildList()

    this.input.addEventListener("focus", () => this.open())
    this.input.addEventListener("click", () => this.open())
    this.input.addEventListener("input", () => this.filter())
    this.input.addEventListener("keydown", (event) => this.onKeydown(event))
    // フォーカスがコントローラの外へ移ったときのみ確定して閉じる
    // （リストのスクロールバー操作等で閉じないよう blur ではなく focusout を使う）。
    this.element.addEventListener("focusout", (event) => {
      if (event.relatedTarget && !this.element.contains(event.relatedTarget)) this.commitAndClose()
    })

    this.onDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.onDocumentClick)
  }

  disconnect() {
    if (this.onDocumentClick) document.removeEventListener("click", this.onDocumentClick)
  }

  // "00:00"〜"23:55" を刻み幅ごとに並べた候補配列を返す（例: ["00:00", "00:05", ...]）。
  buildTimeOptions() {
    const step = this.constructor.STEP_MINUTES
    const options = []
    for (let minutes = 0; minutes < 24 * 60; minutes += step) {
      const hh = String(Math.floor(minutes / 60)).padStart(2, "0")
      const mm = String(minutes % 60).padStart(2, "0")
      options.push(`${hh}:${mm}`)
    }
    return options
  }

  buildList() {
    this.list = document.createElement("ul")
    this.list.className =
      "absolute z-20 mt-1 max-h-52 w-24 list-none overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg hidden"
    this.list.setAttribute("role", "listbox")
    this.element.appendChild(this.list)
    this.input.setAttribute("role", "combobox")
    this.input.setAttribute("aria-expanded", "false")

    // 候補は最大288件×絞り込みごとに再描画されるため、リスナーは <li> 個別ではなく
    // リストへのイベントデリゲーションで1回だけ張る。
    // mousedown で確定（input の blur より先に処理してリストが閉じないようにする）。
    this.list.addEventListener("mousedown", (event) => {
      const item = event.target.closest("li[data-index]")
      if (!item) return
      event.preventDefault()
      this.choose(this.visibleOptions[Number(item.dataset.index)])
    })
    this.list.addEventListener("mouseover", (event) => {
      const item = event.target.closest("li[data-index]")
      if (!item) return
      const index = Number(item.dataset.index)
      if (index === this.activeIndex) return
      this.activeIndex = index
      this.highlight()
    })
  }

  // フォーカス/クリックで全候補を開き、現在値（空なら日中帯の先頭）へスクロールする。
  open() {
    if (!this.list.classList.contains("hidden")) return
    this.showAllOptions()
    this.input.select()
  }

  showAllOptions() {
    this.renderList(this.options)
    const anchor = this.normalizeTime(this.input.value) ?? this.constructor.DEFAULT_SCROLL_TIME
    this.activeIndex = this.nearestOptionIndex(this.visibleOptions, anchor)
    this.highlight({ scrollBlock: "center" })
  }

  close() {
    this.list.classList.add("hidden")
    this.input.setAttribute("aria-expanded", "false")
    this.activeIndex = -1
  }

  // 入力テキストを正規化して確定し、リストを閉じる。
  // 解釈できない入力は最後の有効値へ戻す（空にした場合は空のまま＝時刻クリア）。
  commitAndClose() {
    const raw = this.input.value.trim()
    if (raw === "") {
      this.lastValidValue = ""
    } else {
      const normalized = this.normalizeTime(raw)
      if (normalized) this.lastValidValue = normalized
    }
    this.input.value = this.lastValidValue
    this.close()
  }

  // 入力中は前方一致で候補を絞り込む（"14"→14:00〜14:55、"930"→09:30）。
  // 一致candidateが無い入力（5分刻み外など）はリストを閉じて自由入力に委ねる。
  filter() {
    const digits = this.normalizeChars(this.input.value).replace(/[^0-9]/g, "")
    if (digits === "") {
      this.showAllOptions()
      return
    }
    const matched = this.options.filter((option) => {
      const hhmm = option.replace(":", "")
      return hhmm.startsWith(digits) || hhmm.startsWith(`0${digits}`)
    })
    if (matched.length === 0) {
      this.close()
      return
    }
    this.renderList(matched)
    this.activeIndex = 0
    this.highlight({ scrollBlock: "nearest" })
  }

  renderList(options) {
    this.visibleOptions = options
    this.list.innerHTML = ""
    options.forEach((option, index) => {
      const item = document.createElement("li")
      item.className = this.itemClass(index === this.activeIndex)
      item.textContent = option
      item.setAttribute("role", "option")
      item.dataset.index = index
      this.list.appendChild(item)
    })
    this.list.classList.remove("hidden")
    this.input.setAttribute("aria-expanded", "true")
  }

  itemClass(active) {
    return `cursor-pointer px-3 py-1.5 tabular-nums ${active ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`
  }

  highlight({ scrollBlock = "nearest" } = {}) {
    Array.from(this.list.children).forEach((item, index) => {
      item.className = this.itemClass(index === this.activeIndex)
    })
    const active = this.list.children[this.activeIndex]
    if (active) active.scrollIntoView({ block: scrollBlock })
  }

  choose(option) {
    this.input.value = option
    this.lastValidValue = option
    this.input.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
    this.input.select()
  }

  // anchor（"HH:MM"）以上で最も近い候補の位置を返す。全候補より遅ければ末尾。
  nearestOptionIndex(options, anchor) {
    const index = options.findIndex((option) => option >= anchor)
    return index >= 0 ? index : options.length - 1
  }

  onKeydown(event) {
    // IME 変換中の Enter・矢印は変換操作なのでコントローラでは処理しない。
    if (event.isComposing || event.keyCode === 229) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.move(-1)
        break
      case "Enter":
        if (!this.list.classList.contains("hidden") && this.activeIndex >= 0) {
          // リストを開いたままの Enter は候補確定（フォーム送信しない）。もう一度 Enter で送信できる。
          event.preventDefault()
          this.choose(this.visibleOptions[this.activeIndex])
        } else {
          // リストが閉じた状態の Enter はフォーム送信前に入力値を正規化する。
          this.commitAndClose()
        }
        break
      case "Escape":
        this.commitAndClose()
        break
    }
  }

  move(step) {
    if (this.list.classList.contains("hidden")) {
      this.open()
      return
    }
    const count = this.visibleOptions.length
    if (count === 0) return
    this.activeIndex = (this.activeIndex + step + count) % count
    this.highlight()
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target) && !this.list.classList.contains("hidden")) this.commitAndClose()
  }

  // 全角数字・全角コロンを半角へ寄せる（IME で「１４３０」と打たれても解釈できるように）。
  normalizeChars(value) {
    return value
      .replace(/[０-９]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0xfee0))
      .replace(/：/g, ":")
  }

  // 入力文字列を "HH:MM" へ正規化する。解釈できなければ null を返す。
  // 例: "9"→"09:00", "14"→"14:00", "930"→"09:30", "1430"→"14:30", "9:3"→"09:03", "１４：３０"→"14:30"
  normalizeTime(value) {
    const cleaned = this.normalizeChars(String(value ?? "").trim())
    if (cleaned === "") return null

    let hour
    let minute
    const colonMatch = cleaned.match(/^(\d{1,2}):(\d{1,2})$/)
    if (colonMatch) {
      hour = Number(colonMatch[1])
      minute = Number(colonMatch[2])
    } else if (/^\d{1,4}$/.test(cleaned)) {
      if (cleaned.length <= 2) {
        // 1〜2桁は「時」とみなす（"9"→09:00, "14"→14:00）
        hour = Number(cleaned)
        minute = 0
      } else {
        // 3〜4桁は末尾2桁を「分」とみなす（"930"→09:30, "1430"→14:30）
        hour = Number(cleaned.slice(0, -2))
        minute = Number(cleaned.slice(-2))
      }
    } else {
      return null
    }

    if (hour > 23 || minute > 59) return null
    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
  }
}
